import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import { getCourseName, formatTrialDate, formatTimeSlot } from "../../../helpers/Formatter";

export function BatchDetailsDialog({
  show,
  onHide,
  lead,
  branchId,
  onConfirm,
  confirming,
  confirmError,
  COURSE,
}) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !lead) return;

    if (!lead.course_id || lead.batch_id == null || lead.batch_id === 0) {
      setBatch(null);
      setInstructors([]);
      setError("no-batch");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setBatch(null);
    setInstructors([]);
    setError(null);

    Promise.all([
      axios.get(`api/portal-programs/batches/${lead.course_id}`, {
        params: { branchId, trialDate: lead.trial_date },
      }),
      axios.get(`api/batches/instructors/${lead.batch_id}`),
    ])
      .then(([batchRes, instructorRes]) => {
        if (cancelled) return;
        const found = (batchRes.data.results || []).find(
          (b) => b.batch_id == lead.batch_id
        );
        if (!found) {
          setError("not-found");
        } else {
          setBatch(found);
          setInstructors(instructorRes.data.results || []);
        }
      })
      .catch(() => {
        if (!cancelled) setError("api-error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, lead, branchId]);

  const handleHide = () => {
    setBatch(null);
    setInstructors([]);
    setError(null);
    onHide();
  };

  const handleEdit = () => {
    handleHide();
    history.push(`/customer-leads/${lead.lead_id}/edit-customer-leads`);
  };

  const renderLeadSummary = () => {
    if (!lead) return null;

    const courseName = getCourseName(lead.course_id, COURSE);
    const requestedDate = formatTrialDate(lead.trial_date, lead.day) || "—";
    const timeSlot = formatTimeSlot(lead.start_time, lead.end_time) || "—";

    return (
      <table className="table table-sm table-borderless mb-3">
        <tbody>
          <tr>
            <td className="text-muted">Student</td>
            <td>{lead.student_name}</td>
          </tr>
          <tr>
            <td className="text-muted">Parent</td>
            <td>{lead.lead_name}</td>
          </tr>
          <tr>
            <td className="text-muted">Course/Class</td>
            <td>{courseName}</td>
          </tr>
          <tr>
            <td className="text-muted">Requested Date</td>
            <td>{requestedDate}</td>
          </tr>
          <tr>
            <td className="text-muted">Time Slot</td>
            <td>{timeSlot}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
          <span className="ml-2">Loading batch details…</span>
        </div>
      );
    }

    if (error === "no-batch") {
      return <p className="text-muted">No batch details available for this slot.</p>;
    }

    if (error === "not-found") {
      return <p className="text-muted">Batch not found.</p>;
    }

    if (error === "api-error") {
      return <p className="text-danger">Failed to load batch details. Please try again.</p>;
    }

    if (!batch) return null;

    return (
      <>
        <h6 className="font-weight-bold mb-2">Time Slot Capacity and Coach Details</h6>
        <table className="table table-sm table-borderless mb-3" style={{ maxWidth: 320 }}>
          <thead>
            <tr>
              <th></th>
              <th className="text-center">Current</th>
              <th className="text-center">Max</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Enrolled Students</td>
              <td className="text-center">{batch.student_batch_count ?? "—"}</td>
              <td className="text-center">{batch.maximum_student ?? "—"}</td>
            </tr>
            <tr>
              <td>Trials Scheduled</td>
              <td className="text-center">{batch.trial_count ?? "—"}</td>
              <td className="text-center">{batch.maximum_trial_student ?? "—"}</td>
            </tr>
            <tr>
              <td>Make-ups Scheduled</td>
              <td className="text-center">{batch.makeup_count ?? "—"}</td>
              <td className="text-center">{batch.maximum_makeup_student ?? "—"}</td>
            </tr>
          </tbody>
        </table>
        <h6 className="font-weight-bold mb-2">Instructor(s)</h6>
        {instructors.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {instructors.map((inst, i) => (
              <li key={i}>
                {inst.instructor_name}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-muted">—</span>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={handleHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Review Trial Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderLeadSummary()}
        {renderBody()}
      </Modal.Body>
      <Modal.Footer className="flex-column align-items-stretch">
        {confirmError && (
          <div className="text-danger small mb-2">
            Failed to confirm. Please try again.
          </div>
        )}
        <p className="text-muted small mb-2">
          Any manual changes to the trial date, time, or batch must be completed via Edit — this dialog only confirms the request as scheduled.
        </p>
        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
          <Button variant="secondary" onClick={handleHide} disabled={confirming}>
            Close
          </Button>
          <Button variant="outline-primary" onClick={handleEdit} disabled={confirming}>
            Edit
          </Button>
          <Button
            variant="success"
            onClick={onConfirm}
            disabled={confirming || !!error}
            title={error ? "Batch details could not be loaded" : undefined}
          >
            {confirming ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                {" "}Confirming…
              </>
            ) : (
              "Confirm and Send"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
